import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { usePermission } from '../../hooks/usePermission';
import { money } from '../../utils/format';
import { ErrorNotice, Loading, PageHeader } from '../../components/ui';
import { financialYear } from '../../components/accounts/PeriodBar';

interface TrialRow {
  code: string;
  name: string;
  nature: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  closing_debit: string;
  closing_credit: string;
}

interface Trial {
  rows: TrialRow[];
  totalDebit: string;
  totalCredit: string;
  balanced: boolean;
}

interface ProfitAndLoss {
  totalRevenue: string;
  grossProfit: string;
  totalExpenses: string;
  netProfit: string;
}

/**
 * The shop's accounts on one page.
 *
 * An owner's questions are always the same four: how much cash is in the
 * drawer, what is in the bank, who owes us, and what do we owe. Answering them
 * meant opening four ledgers, so they are here — read from the trial balance,
 * which is the ledger itself rather than a second tally that could drift.
 *
 * Under them, everything the shop owns and owes, each line opening its own
 * ledger. If the books ever stopped balancing this is where it would show,
 * which is why the state of the trial balance is stated at the top rather than
 * buried in a report.
 */
export function AccountsOverviewPage() {
  const { can } = usePermission();
  const navigate = useNavigate();
  const fy = financialYear();

  const trial = useQuery({ queryKey: ['trial-balance'], queryFn: () => api.get<Trial>('/reports/trial-balance') });
  const pl = useQuery({
    queryKey: ['pl-overview', fy.from, fy.to],
    queryFn: () => api.get<ProfitAndLoss>(`/reports/profit-and-loss?from=${fy.from}&to=${fy.to}`),
    enabled: can('report.view_financial'),
  });

  const rows = trial.data?.rows ?? [];
  const net = (code: string): number => {
    const r = rows.find((x) => x.code === code);
    if (!r) return 0;
    return Number(r.closing_debit) - Number(r.closing_credit);
  };
  /** A liability is held as a credit; shown as what the shop owes, positive. */
  const owed = (code: string): number => -net(code);

  const gstPayable = owed('2311') + owed('2312') + owed('2313') - (net('1161') + net('1162') + net('1163'));

  const cards = [
    { label: 'Cash in hand', value: net('1111'), to: '1111', tone: 'bg-success' },
    { label: 'In the bank', value: net('1121'), to: '1121', tone: 'bg-gold-600' },
    { label: 'Customers owe us', value: net('1141'), to: '1141', tone: 'bg-gold-500' },
    { label: 'We owe suppliers', value: owed('2111'), to: '2111', tone: 'bg-danger' },
    { label: 'Held for savers', value: owed('2205'), to: '2205', tone: 'bg-velvet' },
    { label: 'GST payable', value: gstPayable, to: '2311', tone: gstPayable > 0 ? 'bg-danger' : 'bg-silver-600' },
  ];

  const group = (nature: TrialRow['nature']): TrialRow[] =>
    rows
      .filter((r) => r.nature === nature && (Number(r.closing_debit) !== 0 || Number(r.closing_credit) !== 0))
      .sort((a, b) => {
        const av = Math.abs(Number(a.closing_debit) - Number(a.closing_credit));
        const bv = Math.abs(Number(b.closing_debit) - Number(b.closing_credit));
        return bv - av;
      });

  const openLedger = (code: string): void => navigate(`/ledgers?code=${code}`);

  return (
    <>
      <PageHeader
        title="Accounts"
        subtitle="Where the shop's money is, who owes what, and what it has earned — straight from the ledger."
        actions={
          <>
            <button className="btn-ghost" onClick={() => navigate('/journals')}>
              Day book
            </button>
            {can('report.view_financial') ? (
              <button className="btn-primary" onClick={() => navigate('/statements')}>
                Financial statements
              </button>
            ) : null}
          </>
        }
      />
      <ErrorNotice error={trial.error} />
      {trial.isLoading ? <Loading rows={4} /> : null}

      {trial.data ? (
        <>
          <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            {cards.map((c) => (
              <button
                key={c.label}
                onClick={() => openLedger(c.to)}
                className="relative overflow-hidden rounded-xl border border-silver-100 bg-surface px-3 pb-3 pt-2.5 text-left shadow-card hover:shadow-pop"
              >
                <p className="text-2xs leading-tight text-ink-soft">{c.label}</p>
                <p className={`num mt-1 text-lg font-bold ${c.value < 0 ? 'text-danger' : ''}`}>
                  {money(Math.abs(c.value).toFixed(2))}
                  {c.value < 0 ? <span className="ml-1 text-2xs font-normal">in credit</span> : null}
                </p>
                <span className={`absolute inset-x-0 bottom-0 h-1 ${c.tone}`} />
              </button>
            ))}
          </div>

          {/* The year so far, for an owner who wants one number. */}
          {pl.data ? (
            <div className="mb-5 flex flex-wrap items-baseline gap-x-8 gap-y-2 rounded-xl border border-silver-100 bg-surface px-4 py-3 shadow-card">
              <span className="text-2xs uppercase tracking-wide text-ink-soft">This financial year</span>
              <Stat label="Sales" value={money(pl.data.totalRevenue)} />
              <Stat label="Gross profit" value={money(pl.data.grossProfit)} />
              <Stat label="Expenses" value={money(pl.data.totalExpenses)} />
              <Stat
                label={Number(pl.data.netProfit) < 0 ? 'Net loss' : 'Net profit'}
                value={money(Math.abs(Number(pl.data.netProfit)).toFixed(2))}
                tone={Number(pl.data.netProfit) < 0 ? 'bad' : 'good'}
              />
            </div>
          ) : null}

          {/* The everyday jobs, from the page that shows why they matter. */}
          <div className="mb-5 flex flex-wrap gap-2">
            {[
              { label: 'Record an expense', to: '/expenses', perm: 'expense.view' },
              { label: 'Pay a supplier', to: '/payments', perm: 'payment.create' },
              { label: 'Other income', to: '/income', perm: 'income.view' },
              { label: 'Count the till', to: '/cash', perm: 'cash.view' },
              { label: 'Manual entry', to: '/journals', perm: 'accounts.journal_manual' },
              { label: 'Close a month', to: '/periods', perm: 'accounts.view' },
            ]
              .filter((a) => can(a.perm))
              .map((a) => (
                <button key={a.to} className="btn-ghost py-1.5 text-2xs" onClick={() => navigate(a.to)}>
                  {a.label}
                </button>
              ))}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="What the shop owns" rows={group('ASSET')} onOpen={openLedger} side="debit" />
            <Panel title="What the shop owes" rows={group('LIABILITY')} onOpen={openLedger} side="credit" />
            <Panel title="Income" rows={group('INCOME')} onOpen={openLedger} side="credit" />
            <Panel title="Expenses" rows={group('EXPENSE')} onOpen={openLedger} side="debit" />
          </div>

          <p
            className={`mt-5 rounded-lg px-3 py-2 text-center text-sm ${
              trial.data.balanced ? 'bg-success/10 text-success' : 'bg-danger/10 font-semibold text-danger'
            }`}
          >
            {trial.data.balanced
              ? `The books balance — ${money(trial.data.totalDebit)} of debits against the same in credits.`
              : `The books do not balance: ${money(trial.data.totalDebit)} against ${money(trial.data.totalCredit)}.`}
          </p>
        </>
      ) : null}
    </>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'bad' }) {
  return (
    <span>
      <span className="text-ink-soft">{label} </span>
      <span className={`num font-semibold ${tone === 'good' ? 'text-success' : tone === 'bad' ? 'text-danger' : ''}`}>
        {value}
      </span>
    </span>
  );
}

function Panel({
  title,
  rows,
  onOpen,
  side,
}: {
  title: string;
  rows: TrialRow[];
  onOpen: (code: string) => void;
  side: 'debit' | 'credit';
}) {
  const amount = (r: TrialRow): number =>
    side === 'debit'
      ? Number(r.closing_debit) - Number(r.closing_credit)
      : Number(r.closing_credit) - Number(r.closing_debit);
  const total = rows.reduce((a, r) => a + amount(r), 0);

  return (
    <section className="overflow-hidden rounded-xl border border-silver-100 bg-surface shadow-card">
      <header className="flex items-baseline justify-between border-b border-silver-100 bg-gold-50/50 px-4 py-2">
        <h2 className="text-sm font-semibold">{title}</h2>
        <span className="num text-sm font-semibold">{money(total.toFixed(2))}</span>
      </header>
      {rows.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-ink-soft">Nothing here yet.</p>
      ) : (
        <ul>
          {rows.slice(0, 8).map((r) => (
            <li key={r.code}>
              <button
                onClick={() => onOpen(r.code)}
                className="flex w-full items-baseline justify-between border-b border-silver-100/60 px-4 py-2 text-left text-sm last:border-0 hover:bg-gold-50"
              >
                <span>
                  <span className="num mr-2 text-2xs text-ink-faint">{r.code}</span>
                  {r.name}
                </span>
                <span className={`num ${amount(r) < 0 ? 'text-danger' : ''}`}>{money(amount(r).toFixed(2))}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
